// src/pages/ExamSimulatorPage.jsx - CORRECTED
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Flag, CheckCircle, PlayCircle, Clock, AlertTriangle, Volume2, ClipboardList, XCircle, X, Monitor } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import PartDisplay from './PartDisplay.jsx';
import RestrictedAudioPlayer from '../audio/RestrictedAudioPlayer.jsx';
import storageService from '../../services/storageService.js';
import { readSession, writeSession, clearSession, examSessionKey } from '../../utils/sessionStore.js';
import subscriptionService from '../../services/subscriptionService.js';
import freeAttemptService from '../../services/freeAttemptService.js';
import { trackConversionEvent } from '../../utils/conversionEvents.js';
import GuestGateModal from '../auth/GuestGateModal.jsx';
import ExamTimer from './ExamTimer.jsx';
import NoExamSelected from './NoExamSelected.jsx';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import AhaUpsellModal from '../ui/AhaUpsellModal';
import ConfirmDialog from '../ui/ConfirmDialog.jsx';

// Section Instructions Component
const SectionInstructions = ({ section, onStartSection, onHoverStart, onHoverEnd, isHovered }) => (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
        <div className="max-w-3xl w-full bg-white rounded-2xl shadow-xl p-6 sm:p-10 border border-gray-200">
            <div className="text-center mb-6">
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Abschnitt {section.sectionId}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                    {section.sectionName}
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                    Lesen Sie die Anweisungen sorgfältig durch, bevor Sie beginnen.
                </p>
            </div>
            <div
                className="mt-4 sm:mt-8 prose max-w-none prose-gray prose-p:my-2 prose-ul:my-2 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: marked.parse(section.instructions || '') }}
            />
             <div className="mt-6 sm:mt-10 text-center">
              <button
            onClick={onStartSection}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
            className="inline-flex items-center px-4 py-2 sm:px-8 sm:py-3 border-2 border-black text-sm sm:text-lg font-semibold rounded-xl shadow-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transform hover:-translate-y-1 transition-all duration-300 group touch-manipulation relative overflow-hidden"
          >
            {/* Wave fill effect */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: isHovered ? '100%' : '0%',
                background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                transition: 'width 0.7s ease-out',
                zIndex: 0
              }}
            />
            <PlayCircle
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 relative z-10 transition-colors duration-500"
              style={{ color: isHovered ? '#ffffff' : '#000000' }}
            />
            <span
              className="relative z-10 transition-colors duration-500"
              style={{ color: isHovered ? '#ffffff' : '#000000' }}
            >
              Abschnitt beginnen
            </span>
          </button>
            </div>
        </div>
    </div>
);
// Helper function to check if section is speaking
const isSpeakingSection = (sectionId) => {
  if (!sectionId) return false;
  const lowerCaseId = sectionId.toLowerCase();
  return lowerCaseId.includes('speaking') || lowerCaseId.includes('sprechen');
};

const WRITING_TASK_TYPES = new Set([
  'email', 'email-formal', 'email-opinion', 'formal-letter', 'writing', 'essay', 'forum-post',
  'formal-complaint', 'formal-message', 'formal-response-email', 'choice-writing', 'choice-writing-essay'
]);

const TASK_TYPES_WITH_ANSWER_SHEET = new Set([
  'text-reconstruction-endings', 'cloze-free-text', 'cloze-word-bank-click', 'matching-headings',
  'cloze-multiple-choice', 'blog-post-comprehension', 'article-comprehension', 'dual-article-comprehension',
  'dual-article', 'multiple-choice', 'matching-situations', 'opinion-matching', 'rules-comprehension',
  'matching-headlines-opinions', 'matching-questions-to-paragraphs', 'information-transfer-notes', 'fill-blank',
  'opinion-matching-forum', 'podcast-comprehension', 'discussion-comprehension', 'opinion-matching-academic',
  'matching-opinions', 'text-reconstruction-sentence', 'text-reconstruction-paragraph', 'radio-interview',
  'short-texts-announcements', 'short-texts-opinion', 'conversation-comprehension', 'news-broadcast',
  'monologue-comprehension', 'conversation', 'short-texts', 'mixed-comprehension-short-texts'
]);

// Time left to finish the last Hören answers after the recording has ended.
const LISTENING_ANSWER_SECONDS = 60;

const hasObjectiveQuestions = (part) => {
  if (!Array.isArray(part?.questions)) return false;
  return part.questions.some((question) => !question?.isExample);
};

const partSupportsAnswerSheet = (part) => {
  if (!part) return false;
  if (isSpeakingSection(part.sectionId)) return false;
  if (WRITING_TASK_TYPES.has(part.taskType)) return false;
  if (TASK_TYPES_WITH_ANSWER_SHEET.has(part.taskType)) return true;
  return hasObjectiveQuestions(part);
};

// Main Exam Simulator Component - Mobile optimized
const ExamSimulatorPage = ({ user, examData: examDataProp, onFinishExam, onBack, seoH1, onGuestGate, onGuestAuthed }) => {
  const location = useLocation();
  const { t } = useTranslation();
  
  // Try to get examData from navigation state first (if passed from dashboard button)
  // This ensures we use the correct exam even if App.jsx state hasn't updated yet
  const examDataFromState = location.state?.examData || null;
  
  // Use examData from navigation state (if provided), otherwise use prop from App.jsx
  const examData = examDataFromState || examDataProp;
  
  // Safety guard: Show message if no exam is selected
  if (!examData || !examData.examProvider || !examData.level) {
    return <NoExamSelected onBack={onBack} />;
  }

  const allParts = useMemo(() => {
    if (!examData || !Array.isArray(examData.sections)) return [];
    return examData.sections.flatMap(section =>
      (section.parts || []).map(part => ({
        ...part,
        sectionId: section.sectionId,
        sectionName: section.sectionName,
        sectionDuration: section.duration,
        examProvider: examData.examProvider,
        level: examData.level,
        examName: examData.examName
      }))
    );
  }, [examData]);
  // In-progress session persistence: answers/position/timer survive refresh
  // and tab close until the exam is finished or aborted.
  const sessionKey = examSessionKey(user?.uid, examData);
  const restoredSession = useMemo(() => readSession(sessionKey), [sessionKey]);
  const finishedRef = useRef(false);

  // Free plan: exactly ONE exam. The attempt is consumed when an exam is
  // COMPLETED (logExamCompletion writes users/{uid}/usage/exam), not when one
  // is started — an accidental click or a crashed tab must not burn it.
  //
  // Because the flag only exists after a finished exam, an in-progress session
  // by definition has none, so resuming needs no special case. The old
  // `|| restoredSession` short-circuit here skipped the check entirely and
  // never marked anything, which made a forged localStorage key enough to
  // replay the free paper indefinitely.
  //
  // This check is UX, not enforcement: it shows the upsell before the user
  // sinks three hours into an exam they cannot save. The real limit lives in
  // firestore.rules (mayCreateExamResult), so this can afford to fail open.
  const [examAccess, setExamAccess] = useState('checking'); // 'checking' | 'allowed' | 'blocked'
  // Guest gate modal: message step first, then the in-place signup step.

  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      const uid = user?.uid;
      if (!uid) {
        setExamAccess('allowed'); // anonymous /try guest: gated by onGuestGate instead
        return;
      }
      try {
        const status = await subscriptionService.getSubscriptionStatus(uid);
        if (cancelled) return;
        if (!status?.isFree) {
          setExamAccess('allowed');
          return;
        }
        const attempts = await freeAttemptService.getFreeAttempts(uid);
        if (cancelled) return;
        // null means the read FAILED, which is not the same as "no flags".
        // Let them through: the rules refuse the second save regardless, so a
        // network blip can no longer hand out an extra exam.
        if (attempts && freeAttemptService.isExamUsed(attempts)) {
          setExamAccess('blocked');
          return;
        }
        setExamAccess('allowed');
      } catch (error) {
        console.error('Exam access check failed:', error);
        if (!cancelled) setExamAccess('allowed'); // fail-open: never lock users out on errors
      }
    };
    checkAccess();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const [currentPartIndex, setCurrentPartIndex] = useState(restoredSession?.currentPartIndex ?? 0);
  const [userAnswers, setUserAnswers] = useState(restoredSession?.userAnswers ?? {});
  const [flaggedQuestions, setFlaggedQuestions] = useState(restoredSession?.flaggedQuestions ?? []);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isMobileAnswerSheetOpen, setIsMobileAnswerSheetOpen] = useState(false);
  const [answerSheetOnlyNoTexts, setAnswerSheetOnlyNoTexts] = useState(false);
  const [viewingInstructions, setViewingInstructions] = useState(restoredSession ? restoredSession.viewingInstructions !== false : true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const [showMobileWarning, setShowMobileWarning] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  const dismissMobileWarning = () => setShowMobileWarning(false);
  const autoOpenedAnswerSheetForPartRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  /** Listening-audio position ({ sectionId, playsCompleted, time }), kept in the
   *  snapshot so a reload resumes the recording instead of restarting it. */
  const [audioProgress, setAudioProgress] = useState(restoredSession?.audioProgress ?? null);
  const [isListeningSectionStarted, setIsListeningSectionStarted] = useState(restoredSession?.isListeningSectionStarted ?? false);
  const [examStartTime, setExamStartTime] = useState(restoredSession?.examStartTime ?? null);
  // *** FIX: State to store time spent on each section ***
  const [sectionTimes, setSectionTimes] = useState(restoredSession?.sectionTimes ?? {});
    const [isStartSectionHovered, setIsStartSectionHovered] = useState(false);
  const [isNextHovered, setIsNextHovered] = useState(false);
  const [isFinishHovered, setIsFinishHovered] = useState(false);
  /** Tracks last part index so we only show section instructions when moving forward into a new section, not when going back.
   *  Starts at the restored index after a refresh so the mount run of the section-entry effect doesn't re-show instructions or reset the timer. */
  const previousPartIndexRef = useRef(restoredSession ? (restoredSession.currentPartIndex ?? 0) : -1);

  const providerKey = String(examData?.examProvider || '').toUpperCase();
  const providerLabel = providerKey === 'TELC' ? 'ÜBUNGSTEST' : 'MODELLSATZ';

  // Which confirmation is open: 'abort' | 'finish' (the app's
  // dialog, not the browser's confirm box).
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    setExamStartTime(prev => prev ?? Date.now());
  }, [examData]);

  const currentPart = useMemo(() => {
    if (!allParts || allParts.length === 0) return null;
    return allParts[currentPartIndex];
  }, [allParts, currentPartIndex]);

  const currentSection = useMemo(() => {
    if (!currentPart || !examData || !Array.isArray(examData.sections)) return null;
    return examData.sections.find(s => s.sectionId === currentPart.sectionId);
  }, [currentPart, examData]);

  const currentPartHasAnswerSheet = useMemo(
    () => partSupportsAnswerSheet(currentPart),
    [currentPart]
  );

  const examHasAnyAnswerSheet = useMemo(
    () => allParts.some((part) => partSupportsAnswerSheet(part)),
    [allParts]
  );

  useEffect(() => {
  const loadAudioForSection = async () => {
    if (user && currentSection?.sectionId.includes('listening')) {
      setAudioLoading(true);
      setAudioError(null);
      const path = storageService.generateAudioPath(
        examData.examProvider,
        examData.level,
        currentSection.sectionId,
        examData.examFileName,
        examData.variantName
      );
      if (!path) {
        setAudioError("Could not generate an audio path for this section.");
        setAudioLoading(false);
        return;
      }
      try {
        const url = await storageService.getAudioUrl(path);
        if (url) {
          setAudioUrl(url);
        } else {
          setAudioError("Audiodatei nicht gefunden. Bitte überprüfen Sie den Pfad und Dateinamen in Firebase Storage.");
        }
      } catch (error) {
        setAudioError("Fehler beim Laden der Audio-Datei.");
      } finally {
        setAudioLoading(false);
      }
    } else {
      setAudioUrl(null);
    }
  };

  if (currentSection) {
    loadAudioForSection();
  }
}, [currentSection, examData?.examProvider, examData?.level, user]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handle = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    if (!currentPart?.partId || !currentPartHasAnswerSheet || !answerSheetOnlyNoTexts || !isMobile || viewingInstructions) return;
    if (autoOpenedAnswerSheetForPartRef.current === currentPart.partId) return;
    autoOpenedAnswerSheetForPartRef.current = currentPart.partId;
    setIsMobileAnswerSheetOpen(true);
  }, [currentPart?.partId, currentPartHasAnswerSheet, answerSheetOnlyNoTexts, isMobile, viewingInstructions]);

  /** Timer restore (pause-while-away): mid-section we resume the countdown from the
   *  persisted remaining time; on the instructions screen we re-derive the full
   *  section duration so "Abschnitt beginnen" starts a correct timer. */
  const restoredTimer = useMemo(() => {
    if (!restoredSession) return null;
    if (restoredSession.viewingInstructions === false && restoredSession.timeRemaining > 0) {
      return { duration: restoredSession.timeRemaining, running: true };
    }
    if (restoredSession.viewingInstructions !== false) {
      const part = allParts[restoredSession.currentPartIndex ?? 0];
      const secs = parseInt(part?.sectionDuration) * 60;
      return Number.isFinite(secs) && secs > 0 ? { duration: secs, running: false } : null;
    }
    return null;
  }, [restoredSession, allParts]);

  const [currentSectionTimer, setCurrentSectionTimer] = useState(restoredTimer ? restoredTimer.duration : null);
  const [sectionStartTime, setSectionStartTime] = useState(restoredTimer?.running ? Date.now() : null);
  const [timeRemaining, setTimeRemaining] = useState(restoredTimer ? restoredTimer.duration : 0);

  useEffect(() => {
    if (finishedRef.current) return;
    writeSession(sessionKey, {
      userAnswers,
      flaggedQuestions,
      currentPartIndex,
      sectionTimes,
      examStartTime,
      viewingInstructions,
      isListeningSectionStarted,
      timeRemaining,
      audioProgress,
    });
  }, [sessionKey, userAnswers, flaggedQuestions, currentPartIndex, sectionTimes, examStartTime, viewingInstructions, isListeningSectionStarted, timeRemaining, audioProgress]);

  const currentSectionId = currentSection?.sectionId;
  const handleAudioProgress = useCallback(({ playsCompleted, time }) => {
    setAudioProgress({ sectionId: currentSectionId, playsCompleted, time });
  }, [currentSectionId]);
  const restoredAudio = audioProgress?.sectionId === currentSectionId ? audioProgress : null;

  // The Hören recording sets the clock, as in the real exam: it carries the reading
  // time and every repeat, so it is never cut off by the section timer, and once it
  // has finished there is one more minute to complete the last answers.
  const [recordingLength, setRecordingLength] = useState(null);
  const handleRecordingDuration = useCallback((seconds) => {
    setRecordingLength({ sectionId: currentSectionId, seconds });
  }, [currentSectionId]);
  const handleRecordingEnded = useCallback(() => {
    if (!sectionStartTime) return;
    const elapsed = Math.floor((Date.now() - sectionStartTime) / 1000);
    setCurrentSectionTimer((t) => Math.min(t, elapsed + LISTENING_ANSWER_SECONDS));
  }, [sectionStartTime]);
  useEffect(() => {
    if (!sectionStartTime || viewingInstructions || !currentSectionId?.includes('listening')) return;
    if (recordingLength?.sectionId !== currentSectionId || (restoredAudio?.playsCompleted ?? 0) >= 1) return;
    const needed = Math.ceil(recordingLength.seconds - (restoredAudio?.time ?? 0)) + LISTENING_ANSWER_SECONDS;
    if (timeRemaining < needed) setCurrentSectionTimer((t) => t + (needed - timeRemaining));
  }, [sectionStartTime, viewingInstructions, currentSectionId, recordingLength, restoredAudio, timeRemaining]);

  useEffect(() => {
    if (allParts.length === 0) return;

    const currentPart = allParts[currentPartIndex];
    const previousPart = allParts[currentPartIndex - 1];
    const isNewSection = currentPartIndex === 0 || currentPart.sectionId !== previousPart?.sectionId;
    const movedForward = currentPartIndex > previousPartIndexRef.current;

    // *** FIX: Record the time for the previous section when a new one begins ***
    if (isNewSection && currentPartIndex > 0 && sectionStartTime) {
        const endedSectionId = previousPart.sectionId;
        const timeSpentOnSection = Math.round((Date.now() - sectionStartTime) / 1000); // in seconds
        setSectionTimes(prev => ({
            ...prev,
            [endedSectionId]: (prev[endedSectionId] || 0) + timeSpentOnSection
        }));
    }

    // Only show section instructions when moving *forward* into a new section (not when going back within the same section, e.g. Hören part 2 → part 1)
    if (isNewSection && movedForward) {
      const sectionDuration = currentPart.sectionDuration;

      // Skip instructions for sections with duration 0 (combined sections like Sprachbausteine)
      if (sectionDuration === 0 || !sectionDuration) {
        setViewingInstructions(false);
        // Keep existing timer running from previous section
      } else {
        setViewingInstructions(true);
        const durationInSeconds = parseInt(sectionDuration) * 60;
        setCurrentSectionTimer(durationInSeconds);
        setTimeRemaining(durationInSeconds);
        setSectionStartTime(null); // Will be set when section actually starts
      }
    }

    previousPartIndexRef.current = currentPartIndex;
  }, [currentPartIndex, allParts]);

  useEffect(() => {
    if (!sectionStartTime || timeRemaining <= 0 || viewingInstructions) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - sectionStartTime) / 1000);
      const remaining = Math.max(0, currentSectionTimer - elapsed);
      setTimeRemaining(remaining);
      if (remaining === 0) {
        handleSectionTimeExpired();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sectionStartTime, currentSectionTimer, timeRemaining, viewingInstructions]);

  const handleSectionTimeExpired = () => {
    alert(`Zeit für ${allParts[currentPartIndex]?.sectionName} ist abgelaufen! Sie werden automatisch zum nächsten Abschnitt weitergeleitet.`);

    // *** FIX: Record the time for the section that just expired ***
    if (sectionStartTime && currentSection) {
        const timeSpentOnSection = Math.round((Date.now() - sectionStartTime) / 1000);
        setSectionTimes(prev => ({
            ...prev,
            [currentSection.sectionId]: (prev[currentSection.sectionId] || 0) + timeSpentOnSection
        }));
    }

    const currentPart = allParts[currentPartIndex];
    const nextSectionStartIndex = allParts.findIndex((part, index) =>
      index > currentPartIndex && part.sectionId !== currentPart.sectionId
    );

    if (nextSectionStartIndex !== -1) {
      setCurrentPartIndex(nextSectionStartIndex);
    } else {
      handleFinish();
    }
  };

  const handleStartSection = () => {
    setViewingInstructions(false);
    setSectionStartTime(Date.now());
    if (currentSection?.sectionId.includes('listening')) {
      setIsListeningSectionStarted(true);
    }
  };

  const handleAnswerChange = useCallback((partId, answersForPart) => {
    setUserAnswers(prev => ({
      ...prev,
      [partId]: answersForPart,
    }));
  }, []);

  const toggleFlagQuestion = useCallback((questionId) => {
    setFlaggedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  }, []);

  const goToPart = (partIndex) => {
    if (partIndex < 0 || partIndex >= allParts.length) return;
    // Section lock: only allow navigation within the current section (no jumping to Lesen/Hören/Schreiben from another section)
    const targetPart = allParts[partIndex];
    if (currentSection && targetPart?.sectionId !== currentSection.sectionId) return;
    setCurrentPartIndex(partIndex);
    setViewingInstructions(false);
    setIsReviewOpen(false);
    setIsMobileAnswerSheetOpen(false);
    window.scrollTo(0, 0);
  };

  const detailedFlaggedQuestions = useMemo(() => {
    const currentSectionId = currentSection?.sectionId;
    return flaggedQuestions
      .map(qId => {
        let questionInfo = null;
        const partIndex = allParts.findIndex(p => p.questions?.find(q => { if (q.questionId === qId) { questionInfo = q; return true; } return false; }));
        if (questionInfo && partIndex !== -1) {
          return { ...questionInfo, partIndex, partName: allParts[partIndex].partName };
        }
        return null;
      })
      .filter(Boolean)
      .filter(item => allParts[item.partIndex]?.sectionId === currentSectionId);
  }, [flaggedQuestions, allParts, currentSection]);

  const goToNextPart = () => {
    if (currentPartIndex < allParts.length - 1) {
      setCurrentPartIndex(currentPartIndex + 1);
    }
  };

  const goToPreviousPart = () => {
    if (currentPartIndex > 0) {
      setViewingInstructions(false);
      setCurrentPartIndex(currentPartIndex - 1);
    }
  };

  const handleAbortExam = () => setConfirming('abort');
  const abortExam = () => {
    finishedRef.current = true;
    clearSession(sessionKey);
    onBack();
  };

  const handleFinish = () => setConfirming('finish');
  const finishExam = () => {
    const timeSpent = Math.round((Date.now() - examStartTime) / 1000);

    // *** FIX: Record time for the final active section and pass the complete object ***
    let finalSectionTimes = { ...sectionTimes };
    if (sectionStartTime && currentSection) {
        const lastSectionTime = Math.round((Date.now() - sectionStartTime) / 1000);
        finalSectionTimes[currentSection.sectionId] = (finalSectionTimes[currentSection.sectionId] || 0) + lastSectionTime;
    }
    finishedRef.current = true;
    clearSession(sessionKey);
    onFinishExam(userAnswers, flaggedQuestions, timeSpent, finalSectionTimes);
  };

  const progressPercentage = ((currentPartIndex + 1) / allParts.length) * 100;
  const previousPart = currentPartIndex > 0 ? allParts[currentPartIndex - 1] : null;
  const isBackNavigationLocked = currentPartIndex === 0 || (previousPart && currentPart.sectionId !== previousPart.sectionId);

  // Guest gate: anonymous users (/try flow) can do the paper sections
  // (Lesen, Sprachbausteine) free; Hören/Schreiben/Sprechen require an
  // account because audio playback (Storage rules) and AI writing feedback
  // (Cloud Function) only work signed in. All navigation paths (next, answer
  // sheet, timer auto-advance) funnel through currentPartIndex, so this one
  // derived check covers them. The timer is safe: it only runs once
  // viewingInstructions is dismissed, and the gate renders before that.
  const isAuthRequiredSection = (sectionId) => /h(ö|oe?)r|listen|schreib|writ|sprech|speak/i.test(String(sectionId || ''));
  const guestGateActive = typeof onGuestGate === 'function' && !user?.uid
    && !!currentPart && isAuthRequiredSection(currentPart.sectionId);
  useEffect(() => {
    if (guestGateActive) {
      trackConversionEvent('guest_exam_gate_shown', {
        exam_provider: examData?.examProvider,
        exam_level: examData?.level,
        section: currentPart?.sectionId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestGateActive]);

  const totalSectionTime = currentSectionTimer || 0;
  const isWarning = timeRemaining <= totalSectionTime * 0.3 && timeRemaining > totalSectionTime * 0.1;
  const isCritical = timeRemaining <= totalSectionTime * 0.1;

  if (examAccess === 'checking') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
      </div>
    );
  }

  if (examAccess === 'blocked') {
    return (
      <div className="min-h-screen bg-slate-100">
        <AhaUpsellModal
          open={true}
          onDismiss={onBack}
          onUpgrade={() => {}}
          context="exam"
        />
      </div>
    );
  }

  if (guestGateActive) {
    const doneSectionName = previousPart?.sectionName || allParts[0]?.sectionName || 'Lesen';
    return (
      <div className="min-h-screen bg-slate-100 p-2 sm:p-4 lg:p-6 safe-area-inset relative overflow-hidden">
        {/* The next section's instructions stay visible behind the modal
            the overlay blurs them, same presentation as the onboarding modal.
            Inert copy only: pointer events off, hidden from the a11y tree. */}
        <div className="pointer-events-none select-none" aria-hidden="true">
          {currentSection && (
            <SectionInstructions
              section={currentSection}
              onStartSection={() => {}}
              onHoverStart={() => {}}
              onHoverEnd={() => {}}
              isHovered={false}
            />
          )}
        </div>
        <GuestGateModal
          examData={examData}
          doneSectionName={doneSectionName}
          nextSectionName={currentPart?.sectionName}
          onGuestAuthed={onGuestAuthed}
          onGuestGate={onGuestGate}
        />
      </div>
    );
  }

  if (viewingInstructions && currentSection) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center p-2 sm:p-4 lg:p-6 safe-area-inset">
        <AnimatePresence>
          {showMobileWarning && (
            <motion.div
              key="mobile-warning-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 p-6"
              aria-modal="true"
              role="dialog"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 20 }}
                transition={{ type: 'spring', stiffness: 280, damping: 26 }}
                className="relative w-full max-w-sm bg-[radial-gradient(at_top_right,_var(--tw-gradient-stops))] from-slate-50 via-white to-white border border-black/[0.05] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] rounded-2xl sm:rounded-3xl overflow-hidden"
              >
                <div className="flex flex-col items-center text-center px-6 pt-8 pb-7 gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-black flex items-center justify-center shadow-lg shadow-black/20 flex-shrink-0">
                    <Monitor className="w-7 h-7 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-lg font-bold text-black leading-snug">
                      {t('exam.mobileWarning.title')}
                    </h2>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {t('exam.mobileWarning.body')}
                    </p>
                  </div>
                  <div className="w-full flex flex-col gap-2 pt-1">
                    <button
                      onClick={dismissMobileWarning}
                      className="w-full min-h-[48px] rounded-2xl bg-black text-white font-bold text-sm touch-manipulation shadow-lg shadow-black/20 hover:bg-black/90 transition-colors"
                    >
                      {t('exam.mobileWarning.cta')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="w-full max-w-7xl flex-grow flex flex-col bg-white rounded-xl sm:rounded-3xl shadow-2xl shadow-gray-300 border border-gray-200 min-h-[calc(100vh-1rem)] sm:min-h-[calc(100vh-2rem)]">
          <header className="flex-shrink-0 bg-white border-b-4 border-black">
  {/* Top bar - Exam info */}
  <div className="bg-gray-100 border-b-2 border-gray-300 px-4 sm:px-6 py-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="bg-gray-300 px-3 py-1 text-xs font-bold uppercase">
          {examData.level || 'B1'}
        </div>
        <div className="text-sm font-bold uppercase">
          {currentSection?.sectionName || 'LESEN'}
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600 uppercase tracking-wide">
        <span>{providerKey || 'GOETHE'}</span>
        <span>{providerLabel}</span>
      </div>
    </div>
  </div>

  {/* Middle bar - Part info + Timer */}
  <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
    <div>
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold text-gray-900">
          {currentPart?.partName || `Teil ${currentPart?.partNumber}`}
        </h2>
        {currentSection?.duration && (
          <span className="text-sm text-gray-600">
            Arbeitszeit: {currentSection.duration} Minuten
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        {currentPartIndex + 1} von {allParts.length} Teilen
      </div>
    </div>
  </div>

  {/* Bottom bar - Progress */}
  <div className="px-4 sm:px-6 py-2 bg-gray-50 border-t border-gray-200">
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-black h-2 rounded-full transition-all duration-300"
        style={{ width: `${progressPercentage}%` }}
      />
    </div>
  </div>
</header>
          <SectionInstructions
            section={currentSection}
            onStartSection={handleStartSection}
            onHoverStart={() => setIsStartSectionHovered(true)}
        onHoverEnd={() => setIsStartSectionHovered(false)}
        isHovered={isStartSectionHovered}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center p-2 sm:p-4 lg:p-6 safe-area-inset">
      {seoH1 && <h1 className="sr-only">{seoH1}</h1>}
      <div className="w-full max-w-7xl flex-grow flex flex-col bg-white rounded-xl sm:rounded-3xl shadow-2xl shadow-gray-300 border border-gray-200 min-h-[calc(100vh-1rem)] sm:min-h-[calc(100vh-2rem)]">
        <header className="flex-shrink-0 bg-white border-b-4 border-black">
  {/* Single compact bar with all info - stacks on mobile, row on md+ */}
  <div className="bg-gray-100 border-b-2 border-gray-300 px-3 py-2 sm:px-6 sm:py-3">
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
      {/* Left side: Level, Section, Part info */}
      <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-6">
        <div className="flex items-center gap-3">
          <div className="bg-gray-300 px-3 py-1.5 text-xs font-bold uppercase">
            {examData.level || 'B1'}
          </div>
          <div className="text-sm font-bold uppercase tracking-wide">
            {currentSection?.sectionName || 'LESEN'}
          </div>
        </div>

        <div className="border-t border-gray-400 pt-1.5 md:border-t-0 md:border-l-2 md:border-gray-400 md:pl-6 md:pt-0">
          <div className="text-base font-bold text-gray-900">
            {currentPart?.partName || `Teil ${currentPart?.partNumber}`}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {currentPartIndex + 1} von {allParts.length} Teilen
          </div>
        </div>
      </div>

      {/* Right side: Provider info + Abort button */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="flex items-center gap-3 text-xs text-gray-600 uppercase tracking-wide">
          <span>{providerKey || 'GOETHE'}</span>
          <span>{providerLabel}</span>
        </div>
        <button
          type="button"
          onClick={handleAbortExam}
          className="flex items-center gap-1.5 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 border border-red-200 hover:border-red-300 transition-colors touch-manipulation min-h-[44px]"
          aria-label="Prüfung abbrechen"
        >
          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
          <span className="hidden sm:inline">Prüfung abbrechen</span>
        </button>
      </div>
    </div>
  </div>

  {/* Progress bar */}
  <div className="px-3 sm:px-6 py-2 bg-gray-50">
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-black h-2 rounded-full transition-all duration-300"
        style={{ width: `${progressPercentage}%` }}
      />
    </div>
  </div>

</header>
        <main
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-[calc(4rem+max(0.75rem,env(safe-area-inset-bottom,0px)))] md:pb-8"
        >
          {currentSection?.sectionId.includes('listening') && !viewingInstructions && (
            <div className="mb-6">
              <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                <Volume2 className="w-5 h-5 mr-2" />
                Hörverstehen
              </h4>
              {audioLoading && <p>Audio wird geladen...</p>}
              {audioError && <p className="text-red-500">{audioError}</p>}
              {audioUrl && (
                <RestrictedAudioPlayer
                  key={`${currentSectionId}:${audioUrl}`}
                  audioUrl={audioUrl}
                  autoPlay={true}
                  // One recording per Hören section that already contains every repeat,
                  // so it plays once (part.audio.playCount describes the exam, not this file).
                  playCount={1}
                  initialPlaysCompleted={restoredAudio?.playsCompleted ?? 0}
                  initialTime={restoredAudio?.time ?? 0}
                  onProgress={handleAudioProgress}
                  onDuration={handleRecordingDuration}
                  onAudioEnd={handleRecordingEnded}
                />
              )}
            </div>
          )}
          {currentPart && (
    <PartDisplay
        partData={currentPart}
        savedAnswers={userAnswers[currentPart.partId] || {}}
        onAnswerChange={handleAnswerChange}
        flaggedQuestions={flaggedQuestions}
        toggleFlagQuestion={toggleFlagQuestion}
        goToNextPart={goToNextPart}
        allParts={currentSection.parts}
        timeRemaining={timeRemaining}
        currentSection={currentSection}
        isWarning={isWarning}
        isCritical={isCritical}
        isSpeakingSection={isSpeakingSection(currentPart.sectionId)}
        isMobileAnswerSheetOpen={isMobileAnswerSheetOpen}
        onCloseMobileAnswerSheet={() => setIsMobileAnswerSheetOpen(false)}
        onOpenMobileAnswerSheet={() => setIsMobileAnswerSheetOpen(true)}
        onAnswerSheetOnlyNoTexts={setAnswerSheetOnlyNoTexts}
    />
)}
          {/* Desktop only: in-flow nav inside content area (not sticky); no Antwortbogen on desktop */}
          {!viewingInstructions && (
            <nav
              className="hidden md:flex justify-between items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-6 py-4 sm:py-5 mt-6 border-t-2 border-gray-200 bg-gray-50"
              aria-label="Prüfungs-Navigation"
            >
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={goToPreviousPart}
                  disabled={isBackNavigationLocked}
                  className="flex items-center justify-center px-3 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 touch-manipulation min-h-[44px] min-w-[44px]"
                  aria-label="Zurück"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline ml-1">Zurück</span>
                </button>
              </div>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setIsReviewOpen(!isReviewOpen)}
                  className="relative flex items-center justify-center px-3 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl border-2 font-semibold text-yellow-600 border-yellow-400 bg-yellow-50 hover:bg-yellow-100 active:bg-yellow-200 transition-all hover:-translate-y-0.5 touch-manipulation min-h-[44px]"
                  aria-label="Markierte Fragen"
                  aria-expanded={isReviewOpen}
                >
                  <Flag className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-base">Markiert</span>
                  {flaggedQuestions.length > 0 && (
                    <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-yellow-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {flaggedQuestions.length}
                    </span>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-end">
              {(currentPartIndex === allParts.length - 1 || isSpeakingSection(currentPart?.sectionId)) ? (
                <button
                  type="button"
                  onClick={handleFinish}
                  onMouseEnter={() => setIsFinishHovered(true)}
                  onMouseLeave={() => setIsFinishHovered(false)}
                  className="flex items-center justify-center bg-white text-black px-3 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold border-2 border-black transition-all shadow-lg hover:-translate-y-0.5 touch-manipulation min-h-[44px] relative overflow-hidden"
                  aria-label="Prüfung beenden"
                >
                  <div
                    className="absolute inset-0 transition-[width] duration-300 ease-out"
                    style={{
                      width: isFinishHovered ? '100%' : '0%',
                      background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                      zIndex: 0
                    }}
                  />
                  <span className="hidden sm:inline mr-1 sm:mr-2 relative z-10 transition-colors duration-500 text-sm" style={{ color: isFinishHovered ? '#ffffff' : '#000000' }}>
                    Beenden
                  </span>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-500" style={{ color: isFinishHovered ? '#ffffff' : '#000000' }} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={goToNextPart}
                  onMouseEnter={() => setIsNextHovered(true)}
                  onMouseLeave={() => setIsNextHovered(false)}
                  className="flex items-center justify-center bg-white text-black px-3 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold border-2 border-black transition-all shadow-lg hover:-translate-y-0.5 touch-manipulation min-h-[44px] relative overflow-hidden"
                  aria-label="Weiter"
                >
                  <div
                    className="absolute inset-0 transition-[width] duration-300 ease-out"
                    style={{
                      width: isNextHovered ? '100%' : '0%',
                      background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                      zIndex: 0
                    }}
                  />
                  <span className="hidden sm:inline mr-1 sm:mr-2 relative z-10 transition-colors duration-500 text-sm" style={{ color: isNextHovered ? '#ffffff' : '#000000' }}>
                    Weiter
                  </span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-500" style={{ color: isNextHovered ? '#ffffff' : '#000000' }} />
                </button>
              )}
              </div>
            </nav>
          )}
        </main>
        {isReviewOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Markierte Fragen</h3>
                  <button
                    onClick={() => setIsReviewOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 max-h-96 overflow-y-auto">
                {detailedFlaggedQuestions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Keine markierten Fragen</p>
                ) : (
                  <div className="space-y-3">
                    {detailedFlaggedQuestions.map((question, index) => (
                      <div key={question.questionId} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm">
                              Frage {index + 1} - {question.partName}
                            </p>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {typeof question.question === 'string' ? question.question : 'Frage markiert'}
                            </p>
                          </div>
                          <button
                            onClick={() => goToPart(question.partIndex)}
                            className="ml-2 px-3 py-1 bg-yellow-500 text-white text-xs rounded-full hover:bg-yellow-600 transition-colors whitespace-nowrap"
                          >
                            Gehe zu
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {isMobileAnswerSheetOpen && examHasAnyAnswerSheet && !currentPartHasAnswerSheet && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsMobileAnswerSheetOpen(false)} aria-hidden />
            <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col p-4 pb-safe">
              <div className="flex items-center justify-between flex-shrink-0 pb-3 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
                <button
                  type="button"
                  onClick={() => setIsMobileAnswerSheetOpen(false)}
                  className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center"
                  aria-label="Schließen"
                >
                  <X className="w-6 h-6 text-gray-700" />
                </button>
              </div>
              <p className="text-gray-600 py-4">
                In diesem Teil gibt es keinen Antwortbogen. Der Antwortbogen ist in anderen Teilen der Prüfung (z. B. Lesen oder Hören) verfügbar.
              </p>
            </div>
          </div>
        )}
        {!viewingInstructions && (
            <footer
              className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[#E1E4E8]"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <nav
                className="flex items-stretch justify-around h-16 px-2 max-w-lg mx-auto"
                aria-label="Prüfungs-Navigation"
              >
                <button
                  type="button"
                  onClick={goToPreviousPart}
                  disabled={isBackNavigationLocked}
                  className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                  aria-label="Zurück"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
                    <ChevronLeft className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] leading-none font-medium text-[#6B7280]">Zurück</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsReviewOpen(!isReviewOpen)}
                  className="relative flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
                  aria-label="Markierte Fragen"
                  aria-expanded={isReviewOpen}
                >
                  {isReviewOpen && (
                    <div className="absolute -top-0.5 w-5 h-0.5 rounded-full bg-[#EAB308]" />
                  )}
                  <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
                    <Flag className="w-[22px] h-[22px]" style={{ color: isReviewOpen ? '#EAB308' : '#9CA3AF' }} strokeWidth={isReviewOpen ? 2.5 : 2} />
                  </div>
                  <span className="text-[10px] leading-none font-medium" style={{ color: isReviewOpen ? '#EAB308' : '#6B7280' }}>Markiert</span>
                  {flaggedQuestions.length > 0 && (
                    <span className="absolute -top-1 right-1 min-w-[16px] h-4 flex items-center justify-center px-1 rounded-full text-white text-[9px] font-bold bg-[#EAB308]">
                      {flaggedQuestions.length}
                    </span>
                  )}
                </button>
                {examHasAnyAnswerSheet ? (
                  <button
                    type="button"
                    onClick={() => setIsMobileAnswerSheetOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
                    aria-label="Antwortbogen öffnen"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#006D77]/10">
                      <ClipboardList className="w-[22px] h-[22px] text-[#006D77]" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] leading-none font-medium text-[#006D77]">Antwortbogen</span>
                  </button>
                ) : (
                  <div className="min-w-[56px]" aria-hidden />
                )}
                {(currentPartIndex === allParts.length - 1 || isSpeakingSection(currentPart?.sectionId)) ? (
                  <button
                    type="button"
                    onClick={handleFinish}
                    className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
                    aria-label="Prüfung beenden"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-black">
                      <CheckCircle className="w-[22px] h-[22px] text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] leading-none font-medium text-black">Beenden</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goToNextPart}
                    className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
                    aria-label="Weiter"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-black">
                      <ChevronRight className="w-[22px] h-[22px] text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] leading-none font-medium text-black">Weiter</span>
                  </button>
                )}
              </nav>
            </footer>
        )}
      </div>

      {confirming === 'abort' && (
        <ConfirmDialog
          title={t('examSimulator.confirm.abortTitle')}
          onCancel={() => setConfirming(null)}
          actions={[
            { label: t('examSimulator.confirm.keepGoing'), onClick: () => setConfirming(null) },
            { label: t('examSimulator.confirm.abort'), onClick: () => { setConfirming(null); abortExam(); }, danger: true },
          ]}
        >
          {t('examSimulator.confirm.abortBody')}
        </ConfirmDialog>
      )}
      {confirming === 'finish' && (
        <ConfirmDialog
          title={t('examSimulator.confirm.finishTitle')}
          onCancel={() => setConfirming(null)}
          actions={[
            { label: t('examSimulator.confirm.keepGoing'), onClick: () => setConfirming(null) },
            { label: t('examSimulator.confirm.finish'), onClick: () => { setConfirming(null); finishExam(); }, primary: true },
          ]}
        >
          {t('examSimulator.confirm.finishBody')}
        </ConfirmDialog>
      )}
    </div>
  );
};

export default ExamSimulatorPage;